import { useLazyQuery, useMutation, ApolloError } from '@apollo/client';
import { RouteComponentProps, useHistory } from 'react-router';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, FormikHelpers } from 'formik';

import {
	Tabs,
	Spin,
	ErrorPage,
	Notification,
	EditableLayout,
	EditableFields
} from 'Components';
import { GET_GROUP, GET_GROUPS_LIST } from 'Services/Api/Groups/Queries';
import { EDIT_GROUP, EDIT_TENANT } from 'Services/Api/Groups/Mutations';
import {
	GetGroupResponse,
	GetGroupsListResponse,
	GetGroupsListRequest,
	EditTenantRequest,
	EditGroupRequest
} from 'Services/Api/Groups/Types';
import { Details, DomainSettings, ModuleSettings } from './tabs';
import {
	createInitialValues,
	getGroupSubmitValues,
	getTenantSubmitValues
} from './EditGroup.helpers';

import { editTenantSchema, groupSchema } from '../schema';
import { getAppRoutes } from 'Pages/App/App.constants';
import { generatePath } from 'react-router-dom';
import { APIErrorCodes } from 'app-types';
import { checkApolloError } from 'Helpers/graphql';
import { useConfigs, useErrorBoundary } from 'Hooks';
import { EditGroupValues } from '../Group.types';
import {
	assignHierarchyLabel,
	getHierarchyLabel
} from 'Services/Api/HierarchyLabel/HierarchyLabelService';
import { HierarchyLabelType, getAllowedChildLabels, isLeafLabel } from 'app-config';

export enum TabKey {
	Details = 'Details',
	DomainSettings = 'Domain settings',
	ModuleSettings = 'Module settings'
}
const TABS_TITLES = {
	[TabKey.Details]: 'group.editGroup.sections.details',
	[TabKey.DomainSettings]: 'group.editGroup.sections.domainSettings',
	[TabKey.ModuleSettings]: 'group.editGroup.sections.moduleSettings'
};
interface EditGroupParams {
	groupId: string;
}

type EditGroupProps = RouteComponentProps<EditGroupParams>;

const EditGroup: FC<EditGroupProps> = (props) => {
	const history = useHistory();
	const { t } = useTranslation();
	const errorBoundary = useErrorBoundary();
	const [savedHierarchyLabel, setSavedHierarchyLabel] = useState<string | undefined>(undefined);
	const [hierarchyLabelSource, setHierarchyLabelSource] = useState<string | undefined>(undefined);
	const [hierarchyLabelLoading, setHierarchyLabelLoading] = useState(true);
	const [nodeHasChildren, setNodeHasChildren] = useState(false);
	const [getChildGroups, childGroupsResult] = useLazyQuery<GetGroupsListResponse, GetGroupsListRequest>(
		GET_GROUPS_LIST,
		{ fetchPolicy: 'no-cache' }
	);
	const [getGroup, response] = useLazyQuery<GetGroupResponse>(GET_GROUP, {
		variables: {
			id: Number(props.match.params.groupId)
		},
		onError: (error: ApolloError) => {
			const apolloError = checkApolloError(error);
			if (apolloError.is(APIErrorCodes.GroupsNotInHierarchy)) {
				errorBoundary.setStatus('forbidden');
			} else {
				errorBoundary.onError(error);
			}
		}
	});
	const configs = useConfigs();
	const [editTenant] = useMutation<EditTenantRequest>(EDIT_TENANT);
	const [editGroup] = useMutation<EditGroupRequest>(EDIT_GROUP);

	const fetchHierarchyLabel = useCallback(async (nodeId: number) => {
		try {
			setHierarchyLabelLoading(true);
			const result = await getHierarchyLabel(nodeId);
			if (result.success && result.data?.node_type) {
				setSavedHierarchyLabel(result.data.node_type);
				setHierarchyLabelSource(result.data.node_type_source);
			}
		} catch (err) {
			console.error('Failed to fetch hierarchy label:', err);
		} finally {
			setHierarchyLabelLoading(false);
		}
	}, []);

	useEffect(() => {
		getGroup();
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		const groupId = Number(props.match.params.groupId);
		if (groupId) {
			fetchHierarchyLabel(groupId);
			// Check if this node has children (for Sub-Group validation)
			getChildGroups({
				variables: {
					sort: 'name',
					filters: { depth: 1, parentId: groupId }
				}
			});
		} else {
			setHierarchyLabelLoading(false);
		}
		// eslint-disable-next-line
	}, [props.match.params.groupId]);

	useEffect(() => {
		const children = childGroupsResult.data?.listGroups;
		if (children && children.length > 0) {
			setNodeHasChildren(true);
		}
	}, [childGroupsResult.data]);

	const onSubmit = async (
		values: EditGroupValues,
		helpers: FormikHelpers<EditGroupValues>
	) => {
		// Hard rule: Sub-Group nodes must not have children (leaf node)
		if (values.hierarchyLabel === 'Sub-Group' && nodeHasChildren) {
			helpers.setSubmitting(false);
			Notification.error({
				description: 'Cannot assign "Sub-Group" to a node that has child groups. Remove children first or choose a different label.'
			});
			return;
		}

		// â”€â”€ Validate chosen label is compatible with existing children â”€â”€
		if (nodeHasChildren && values.hierarchyLabel) {
			const children = childGroupsResult.data?.listGroups || [];
			
			// Fetch hierarchy labels for all children
			const childLabels = new Set<string>();
			for (const child of children) {
				try {
					const childLabelRes = await getHierarchyLabel(Number(child.id));
					if (childLabelRes.success && childLabelRes.data?.node_type) {
						childLabels.add(childLabelRes.data.node_type);
					}
				} catch (err) {
					console.warn('Could not fetch child hierarchy label:', err);
				}
			}

			// Check if the new parent label allows all existing child labels
			const allowedChildLabels = getAllowedChildLabels(values.hierarchyLabel);
			const invalidChildren: string[] = [];
			
			childLabels.forEach((childLabel) => {
				if (!allowedChildLabels.includes(childLabel as HierarchyLabelType)) {
					invalidChildren.push(childLabel);
				}
			});

			if (invalidChildren.length > 0) {
				helpers.setSubmitting(false);
				const childCount = children.length;
				Notification.error({
					description: (
						<div>
							<strong>Cannot change Type to "{values.hierarchyLabel}"</strong>
							<p style={{ marginTop: 8 }}>
								This node has {childCount} child {childCount === 1 ? 'group' : 'groups'} with the following Type{invalidChildren.length > 1 ? 's' : ''}:
							</p>
							<ul style={{ marginTop: 8, paddingLeft: 20 }}>
								{Array.from(invalidChildren).map((label, idx) => (
									<li key={idx}>{label}</li>
								))}
							</ul>
							<p style={{ marginTop: 8 }}>
								"{values.hierarchyLabel}" only allows: {allowedChildLabels.join(', ')}.
							</p>
							<p style={{ marginTop: 8 }}>
								Please either change the child Types first or choose a different Type for this node.
							</p>
						</div>
					),
					duration: 15
				});
				return;
			}
		}

		// â”€â”€ Validate chosen label against parent's hierarchy constraints â”€â”€
		if (values.parentId && values.hierarchyLabel) {
			try {
				const parentLabelRes = await getHierarchyLabel(values.parentId as number);
				if (parentLabelRes.success && parentLabelRes.data?.node_type) {
					const parentLabel = parentLabelRes.data.node_type;

					if (isLeafLabel(parentLabel)) {
						helpers.setSubmitting(false);
						Notification.error({
							description: 'The parent node is labeled "Sub-Group" (leaf node) and cannot have children. Please move this group or change the parent\'s label first.'
						});
						return;
					}

					const allowed = getAllowedChildLabels(parentLabel);
					if (!allowed.includes(values.hierarchyLabel as HierarchyLabelType)) {
						helpers.setSubmitting(false);
						Notification.error({
							description: `"${values.hierarchyLabel}" is not allowed under a "${parentLabel}" node. Allowed labels: ${allowed.join(', ')}.`
						});
						return;
					}
				}
			} catch (err) {
				console.warn('Could not verify parent hierarchy label:', err);
			}
		}

		try {
			if (!values.parentId) {
				await editTenant({
					variables: {
						input: getTenantSubmitValues(values)
					}
				});
			} else {
				await editGroup({
					variables: {
						input: getGroupSubmitValues(values)
					}
				});
			}

			// Call Hierarchy Label API if hierarchyLabel is set
			if (values.id && values.hierarchyLabel) {
				try {
					const labelResult = await assignHierarchyLabel({
						nodeId: Number(values.id),
						nodeType: values.hierarchyLabel as HierarchyLabelType
					});
					if (labelResult && !labelResult.success) {
						helpers.setSubmitting(false);
						Notification.error({
							description: labelResult.message || 'Failed to assign hierarchy label. Please try again.'
						});
						return;
					}
				} catch (labelError) {
					helpers.setSubmitting(false);
					console.error('Failed to assign hierarchy label:', labelError);
					Notification.error({
						description: 'Failed to assign hierarchy label. Group was saved but labeling failed. Please retry.'
					});
					return;
				}
			}

			goBack();

			Notification.success({
				description: t('notification.edit.success')
			});
		} catch (error) {
			helpers.setSubmitting(false);
			const apolloError = checkApolloError(error);

			if (
				apolloError.is(APIErrorCodes.GroupHierarchyDepthLImitExceeded)
			) {
				Notification.error({
					description: configs.message
				});
				return;
			}

			if (
				apolloError.is(APIErrorCodes.GroupSyncCodeShouldBeUniqPerTenant)
			) {
				helpers.setErrors({
					syncCode: t(
						'group.fieldErrorCodes.GROUP_SYNC_CODE_SHOULD_BE_UNIQUE_PER_TENANT',
						{ path: 'Group sync code' }
					)
				});
				return;
			}

			if (apolloError.is(APIErrorCodes.GroupCannotBeProperty)) {
				Notification.error({
					description: t('errorCodes.GROUP_CAN_NOT_BE_PROPERTY', {
						defaultValue:
							'This change is not allowed for your organization. Please contact your administrator.'
					})
				});
				return;
			}

			if (
				apolloError.is(
					APIErrorCodes.MovingGroupsBetweenTenantsForbidden
				)
			) {
				Notification.error({
					description: t(
						`errorCodes.${APIErrorCodes.MovingGroupsBetweenTenantsForbidden}`
					)
				});
				return;
			}

			const fieldErrors = apolloError.getFieldErrors(
				t,
				validationSchema.describe().fields,
				'group.fieldErrorCodes'
			);

			helpers.setErrors(fieldErrors || {});
			if (fieldErrors) return;
			const errors = apolloError.getNonFieldErrors(
				t,
				'groups.groupDetails.components.moveGroupModal.errorCodes',
				{
					[APIErrorCodes.GroupsHierarchySizeLimitExceeded]: {
						groupsHierarchySizeLimit: String(
							configs.groupsHierarchySizeLimit
						)
					}
				}
			);

			if (errors.length) {
				Notification.error({
					description: errors
				});
				return;
			}
			Notification.error({
				description: t('errorCodes.genericErrorMessage')
			});
		}
	};

	const group = response.data?.getGroup ?? null;

	const validationSchema = useMemo(() => {
		const getValidationSchema = group?.isTenant
			? editTenantSchema
			: groupSchema;
		return getValidationSchema(configs.maxGroupNameCharacters);
	}, [group?.isTenant, configs.maxGroupNameCharacters]);

	const goBack = () => {
		history.push(
			generatePath(getAppRoutes().GROUPS, { groupId: group?.id })
		);
	};

	// Show spinner while the group query hasn't completed yet OR hierarchy label is loading
	if (!response.called || response.loading || hierarchyLabelLoading) {
		return <Spin />;
	}

	if (response.error || !group) {
		return <ErrorPage status={404} />;
	}

	const heading = t('group.editGroup.title', {
		entity: t(group.isTenant ? 'group.tenant' : 'group.group'),
		level: group.level,
		name: group.name
	});
	const fallbackValues = createInitialValues(group);
	const initialValues = {
		...fallbackValues,
		// Use saved label from hierarchy-label backend if available;
		// otherwise fall back to the is_new-aware default from createInitialValues
		hierarchyLabel: savedHierarchyLabel || fallbackValues.hierarchyLabel
	};

	return (
		<Formik
			validationSchema={validationSchema}
			initialValues={initialValues}
			onSubmit={onSubmit}
		>
			<EditableLayout.Tab goBack={goBack} heading={heading}>
				<Tabs defaultActiveKey={TabKey.Details}>
					<Tabs.TabPane
						tab={t(TABS_TITLES[TabKey.Details])}
						key={TabKey.Details}
					>
						<EditableFields>
							<Details
								isTenant={group.isTenant}
								groupId={props.match.params.groupId}
								hierarchyLabelSource={hierarchyLabelSource}
							/>
						</EditableFields>
					</Tabs.TabPane>
					{group.isTenant ? (
						<Tabs.TabPane
							tab={t(TABS_TITLES[TabKey.DomainSettings])}
							key={TabKey.DomainSettings}
						>
							<EditableFields>
								<DomainSettings />
							</EditableFields>
						</Tabs.TabPane>
					) : null}

					<Tabs.TabPane
						tab={t(TABS_TITLES[TabKey.ModuleSettings])}
						key={TabKey.ModuleSettings}
					>
						<EditableFields>
							<ModuleSettings isTenant={group.isTenant} />
						</EditableFields>
					</Tabs.TabPane>
				</Tabs>
			</EditableLayout.Tab>
		</Formik>
	);
};

export default EditGroup;